import React, {useState, useRef, useEffect} from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  Card,
  CardMedia,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {CloudUpload as CloudUploadIcon, Delete as DeleteIcon, AddPhotoAlternate as AddPhotoIcon, ZoomIn as ZoomInIcon} from "@mui/icons-material";
import {useTranslation} from "react-i18next";

interface ImageUploadProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
  onUpload: (files: File[]) => Promise<string[]>;
  onRemove: (imageUrl: string) => Promise<void>;
  maxImages?: number;
  disabled?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({images = [], onImagesChange, onUpload, onRemove, maxImages = 10, disabled = false}) => {
  const {t} = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageUrls, setImageUrls] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState<{[key: string]: boolean}>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch images using authenticated API
  useEffect(() => {
    images.forEach((s3Key) => {
      if (!imageUrls[s3Key] && !s3Key.startsWith("blob:") && !s3Key.startsWith("http")) {
        setLoading((prev) => ({...prev, [s3Key]: true}));
        const token = localStorage.getItem("token");

        const baseUrl =
          process.env.NODE_ENV === "production" ? process.env.REACT_APP_API_URL || "https://api.smartfarm.com" : "http://localhost:3001";

        fetch(`${baseUrl}/api/ponds/images/${encodeURIComponent(s3Key)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "image/*",
          },
        })
          .then((res) => {
            if (!res.ok) throw new Error("Failed to fetch image");
            return res.blob();
          })
          .then((blob) => {
            const reader = new FileReader();
            reader.onload = () => {
              const dataUrl = reader.result as string;
              setImageUrls((prev) => ({...prev, [s3Key]: dataUrl}));
            };
            reader.readAsDataURL(blob);
          })
          .catch((error) => {
            console.error("Failed to fetch image for:", s3Key, error);
            setImageUrls((prev) => ({...prev, [s3Key]: ""}));
          })
          .finally(() => {
            setLoading((prev) => ({...prev, [s3Key]: false}));
          });
      }
    });
  }, [images]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Validate file count
    if (images.length + files.length > maxImages) {
      setError(`Maximum ${maxImages} images allowed`);
      return;
    }

    // Validate file types
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    const invalidFiles = files.filter((file) => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      setError("Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.");
      return;
    }

    // Validate file sizes (10MB per file)
    const maxSize = 10 * 1024 * 1024; // 10MB
    const oversizedFiles = files.filter((file) => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      setError("File size too large. Maximum 10MB per image.");
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const uploadedUrls = await onUpload(files);
      onImagesChange([...images, ...uploadedUrls]);
    } catch (err: any) {
      setError(err.message || "Failed to upload images");
    } finally {
      setUploading(false);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = async (imageUrl: string) => {
    try {
      await onRemove(imageUrl);
      onImagesChange(images.filter((img) => img !== imageUrl));
    } catch (err: any) {
      setError(err.message || "Failed to remove image");
    }
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };

  const handleCloseDialog = () => {
    setSelectedImage(null);
  };

  const getImageUrl = (s3Key: string) => {
    // If it's already a full URL or blob URL, return as is
    if (s3Key.startsWith("http") || s3Key.startsWith("blob:")) {
      return s3Key;
    }
    // Return the authenticated data URL if available
    return imageUrls[s3Key] || "";
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      )}

      {/* Upload Button */}
      <Box sx={{mb: 2}}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          style={{display: "none"}}
          disabled={disabled || uploading}
        />
        <Button
          variant="outlined"
          startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading || images.length >= maxImages}
          sx={{mb: 1}}>
          {uploading ? t("Uploading...") : t("Upload Images")}
        </Button>
        <Typography variant="caption" color="text.secondary" display="block">
          {t("Maximum {{maxImages}} images, 10MB each", {maxImages})}
        </Typography>
      </Box>

      {/* Image Grid */}
      {images.length > 0 && (
        <Grid container spacing={2}>
          {images.map((imageUrl, index) => {
            const src = getImageUrl(imageUrl);
            const isLoading = loading[imageUrl];

            return (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <Card sx={{position: "relative", height: 200}}>
                  {isLoading ? (
                    <Box
                      sx={{
                        height: 140,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "grey.100",
                      }}>
                      <CircularProgress size={30} />
                    </Box>
                  ) : src ? (
                    <CardMedia
                      component="img"
                      height="140"
                      image={src}
                      alt={`Image ${index + 1}`}
                      sx={{
                        cursor: "pointer",
                        objectFit: "cover",
                        height: "140px",
                      }}
                      onClick={() => handleImageClick(imageUrl)}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 140,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "grey.100",
                        color: "text.secondary",
                      }}>
                      <Typography variant="caption">Failed to load</Typography>
                    </Box>
                  )}
                  <Box sx={{p: 1, display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    <Typography variant="caption" noWrap>
                      {t("Image")} {index + 1}
                    </Typography>
                    <Box>
                      <IconButton size="small" onClick={() => handleImageClick(imageUrl)} sx={{mr: 0.5}}>
                        <ZoomInIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleRemoveImage(imageUrl)} disabled={disabled}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Image Preview Dialog */}
      <Dialog open={!!selectedImage} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{t("Image Preview")}</DialogTitle>
        <DialogContent>
          {selectedImage && (
            <img
              src={getImageUrl(selectedImage)}
              alt="Preview"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("Close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageUpload;
